import { useEffect } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Button, Chip, Stack, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import DescriptionIcon from '@mui/icons-material/Description';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';
import useApiHook from "../../hooks/useApiHook";
import useAppView from "../platforms/useAppView";

const formatAlertType = (value) => {
    if (!value) {
        return null;
    }

    return String(value)
        .split('_')
        .filter(Boolean)
        .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
        .join(' ');
};

const formatAlertLevel = (value) => {
    if (!value) {
        return null;
    }

    const normalized = String(value).toLowerCase();

    if (normalized === 'high') {
        return 'Alta';
    }

    if (normalized === 'moderate') {
        return 'Moderata';
    }

    if (normalized === 'low') {
        return 'Bassa';
    }

    return String(value);
};

const renderAlertContent = (alertItem) => {
    if (typeof alertItem === 'string') {
        return <Typography variant="body2">{alertItem}</Typography>;
    }

    if (!alertItem || typeof alertItem !== 'object') {
        return <Typography variant="body2">{String(alertItem)}</Typography>;
    }

    const titleParts = [formatAlertType(alertItem.type), formatAlertLevel(alertItem.level)].filter(Boolean);
    const evidence = Array.isArray(alertItem.evidence) ? alertItem.evidence.filter(Boolean) : [];

    return (
        <Stack spacing={0.75}>
            {titleParts.length > 0 && (
                <Typography variant="subtitle2" fontWeight="bold">
                    {titleParts.join(' - ')}
                </Typography>
            )}
            {alertItem.description && (
                <Typography variant="body2">
                    {alertItem.description}
                </Typography>
            )}
            {evidence.length > 0 && (
                <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }}>
                    {evidence.map((item, index) => (
                        <Typography component="li" variant="body2" key={`${index}-${item}`}>
                            {item}
                        </Typography>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

const renderTextItems = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
        return null;
    }

    return (
        <Stack spacing={1}>
            {items.map((item, index) => (
                <Typography variant="body2" key={`${index}-${typeof item === 'string' ? item : JSON.stringify(item)}`}>
                    {typeof item === 'string' ? item : JSON.stringify(item)}
                </Typography>
            ))}
        </Stack>
    );
};

const renderSections = (sections) => {
    if (!Array.isArray(sections) || sections.length === 0) {
        return null;
    }

    return (
        <Stack spacing={2}>
            {sections.map((section, index) => (
                <Stack spacing={1} key={`${section.title || 'section'}-${index}`}>
                    <Typography variant="subtitle2" fontWeight="bold">{section.title}</Typography>
                    {renderTextItems(section.items)}
                </Stack>
            ))}
        </Stack>
    );
};

const formatResultStatus = (value) => {
    if (String(value).toLowerCase() === 'completed') {
        return 'completa';
    }

    return value;
};

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
        const base64String = reader.result
            .replace('data:', '')
            .replace(/^.+,/, '');
        resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const PatientAssistantResultCard = ({ result, jobId, compact = false }) => {
    const payload = result?.payload || {};
    const { response, headers, makeRequest } = useApiHook();
    const { isNativePlatform } = useAppView();

    const downloadResultHandler = (ext) => {
        if (!jobId || !result?.id) {
            return;
        }

        makeRequest(`/ai/jobs/${jobId}/results/${result.id}/${ext}`, 'get', {}, { responseType: "blob" });
    };

    useEffect(() => {
        if (!response || !headers?.['x-file-name'] || !headers?.['x-file-ext']) {
            return;
        }

        const fileName = `${headers['x-file-name']}.${headers['x-file-ext']}`;

        if (isNativePlatform) {
            blobToBase64(response).then(async (base64Data) => {
                const mimeMap = {
                    pdf: 'application/pdf',
                    txt: 'text/plain',
                    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                };
                const ext = fileName.split('.').pop();
                const mimeType = mimeMap[ext] || 'application/octet-stream';
                const writeResult = await Filesystem.writeFile({
                    path: fileName,
                    data: base64Data,
                    directory: Directory.Cache
                });
                await FileOpener.openFile({
                    path: writeResult.uri,
                    mimeType
                });
            }).catch((error) => {
                console.error("Errore apertura file risultato AI", error);
            });
            return;
        }

        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, [headers, isNativePlatform, response]);

    return (
        <Accordion
            disableGutters
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                '&:before': {
                    display: 'none'
                }
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack spacing={0.75} sx={{ width: '100%', pr: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight="bold">{result.title}</Typography>
                        <Chip label={formatResultStatus(result.status)} size="small" />
                    </Stack>
                </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0, pb: compact ? 2 : 3 }}>
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button variant="outlined" color="error" size="small" startIcon={<PictureAsPdfIcon />} onClick={() => downloadResultHandler('pdf')}>
                            PDF
                        </Button>
                        <Button variant="outlined" color="error" size="small" startIcon={<FormatAlignLeftIcon />} onClick={() => downloadResultHandler('txt')}>
                            TXT
                        </Button>
                        <Button variant="outlined" color="error" size="small" startIcon={<DescriptionIcon />} onClick={() => downloadResultHandler('doc')}>
                            DOC
                        </Button>
                    </Stack>

                    {payload.overview && (
                        <Typography variant="body1">{payload.overview}</Typography>
                    )}

                    {renderTextItems(payload.bullets)}
                    {renderSections(payload.sections)}

                    {Array.isArray(payload.alerts) && payload.alerts.length > 0 && (
                        <Stack spacing={1}>
                            <Typography variant="subtitle2" fontWeight="bold">Alert</Typography>
                            {payload.alerts.map((alertItem, index) => (
                                <Alert severity="warning" key={`${index}-${JSON.stringify(alertItem)}`}>
                                    {renderAlertContent(alertItem)}
                                </Alert>
                            ))}
                        </Stack>
                    )}

                    {Array.isArray(payload.timeline) && payload.timeline.length > 0 && (
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle2" fontWeight="bold">Timeline</Typography>
                            {renderTextItems(payload.timeline)}
                        </Stack>
                    )}

                    {payload.disclaimer && (
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                            {payload.disclaimer}
                        </Typography>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};

export default PatientAssistantResultCard;
